import { Koi } from '@ksyos/koi';
import type { IAllergies } from './allergy';
import type { IAssets } from './assets';
import type { IDiagnosisAddEditValidated } from './diagnosis';
import type { IEpisodesAndJournals } from './episodesAndJournal';
import type { ILabResults } from './labResult';
import type { Medications } from './medication';
import type { ISchemaContainer } from './schemaContainer';
import { RequestValidationTypes } from '../enums/requestValidationTypes';
import { allergies } from './allergy';
import { assets } from './assets';
import {
    tcchV1DraftRequest,
    tcchV1FinalRequest,
    tcciV1DraftRequest,
    tcciV1FinalRequest,
    tccrV1DraftRequest,
    tccrV1FinalRequest,
    tcohV2DraftRequest,
    tcohV2FinalRequest,
    tcorV1DraftRequest,
    tcorV1FinalRequest,
    tiecgV1DraftRequest,
    tiecgV1FinalRequest,
} from './customControls/cardiology/holterConsult/cardiology';
import {
    dermaCscV1DraftRequest,
    dermaCscV1FinalRequest,
    dermaCV1DraftRequest,
    dermaCV1FinalRequest,
    dermaCwondV1DraftRequest,
    dermaCwondV1FinalRequest,
    dermaOthV1DraftRequest,
    dermaOthV1FinalRequest,
    dermaSnelCscV1DraftRequest,
    dermaSnelCscV1FinalRequest,
    dermaSnelCV1DraftRequest,
    dermaSnelCV1FinalRequest,
} from './customControls/dermatology';
import { gtcV1DraftRequest, gtcV1FinalRequest, gtoV1DraftRequest, gtoV1FinalRequest } from './customControls/general';
import {
    ggzbTherV1DraftRequest,
    ggzbTherV1FinalRequest,
    ggzcf2fV1DraftRequest,
    ggzcf2fV1FinalRequest,
    ggzcgV1DraftRequest,
    ggzcgV1FinalRequest,
    ggzcmdoV1DraftRequest,
    ggzcmdoV1FinalRequest,
    ggzctriageV1DraftRequest,
    ggzctriageV1FinalRequest,
    ggzctV1DraftRequest,
    ggzctV1FinalRequest,
    ggzoEmblV1DraftRequest,
    ggzoEmblV1FinalRequest,
    ggzoIntV1DraftRequest,
    ggzoIntV1FinalRequest,
    ggzvV1DraftRequest,
    ggzvV1FinalRequest,
} from './customControls/mentalHealth';
import {
    orthCV1DraftRequest,
    orthCV1FinalRequest,
    orthOFolV1DraftRequest,
    orthOFolV1FinalRequest,
    orthOV1DraftRequest,
    orthOV1FinalRequest,
    tfcV1DraftRequest,
    tfcV1FinalRequest,
    tfoV1DraftRequest,
    tfoV1FinalRequest,
    tocV1DraftRequest,
    tocV1FinalRequest,
    tooV1DraftRequest,
    tooV1FinalRequest,
    tovV1FinalRequest,
} from './customControls/ophthalmology';
import {
    pilotConsultationV1FinalRequest,
    pilotContactV1FinalRequest,
    pilotExaminationV1FinalRequest,
    pilotReferralSmallV1FinalRequest,
    pilotReferralV1FinalRequest,
} from './customControls/pilot';
import { tpcV1DraftRequest, tpcV1FinalRequest } from './customControls/pulmonology';
import {
    osasendV1DraftRequest,
    osasendV1FinalRequest,
    osaseV1DraftRequest,
    osaseV1FinalRequest,
    osaseV2DraftRequest,
    osaseV2FinalRequest,
    osasnV1DraftRequest,
    osasnV1FinalRequest,
    osasoV1DraftRequest,
    osasoV1FinalRequest,
    osasqV1DraftRequest,
    osasqV1FinalRequest,
    osasvcV1DraftRequest,
    osasvcV1FinalRequest,
    osasvcV2DraftRequest,
    osasvcV2FinalRequest,
    osasvoV1DraftRequest,
    osasvoV1FinalRequest,
    osasvV1DraftReferralSmall,
    osasvV1FinalReferralSmall,
} from './customControls/somnology';
import { diagnosisSchema } from './diagnosis';
import { episodesAndJournalEntriesSchema } from './episodesAndJournal';
import { labResults } from './labResult';
import { medications } from './medication';

export interface IRequestEditRequesterValidated {
    requesterOrganizationId: string;
    requesterPractitionerId: string;
}

const requestEditRequester = Koi.object().keys({
    requesterOrganizationId: Koi.string().uuid(),
    requesterPractitionerId: Koi.string().uuid(),
});

export const requestEditRequesterSchema: ISchemaContainer<IRequestEditRequesterValidated> = {
    schema: requestEditRequester.options({ convert: false, presence: 'required' }),
};

export interface IRequestEditRecipientValidated {
    recipientOrganizationId: string;
    recipientPractitionerId: string;
}

const requestEditRecipient = Koi.object().keys({
    recipientOrganizationId: Koi.string().uuid(),
    recipientPractitionerId: Koi.string().uuid(),
});

export const requestEditRecipientSchema: ISchemaContainer<IRequestEditRecipientValidated> = {
    schema: requestEditRecipient.options({ convert: false, presence: 'required' }),
};

export interface IRequestStatusCancelOrAbortValidated {
    reasonStopProccess: string;
}

const requestStatusCancelOrAbort = Koi.object().keys({
    reasonStopProccess: Koi.string().max(255),
});

export const requestStatusCancelOrAbortSchema: ISchemaContainer<IRequestStatusCancelOrAbortValidated> = {
    schema: requestStatusCancelOrAbort.options({ convert: false, presence: 'required' }),
};

export interface IRequestValidated {
    customControlData: any;
    draftCareProviderOrganizationId: string | null;
    draftCareProviderPractitionerId: string | null;
    id?: string;
    patientId: string;
    previousRequestId: string | null;
    question: string | null;
    selectedDiagnosticReportConsumerPractitionerId?: string | null;
    selectedDiagnosticReportProviderOrganizationId: string | null;
    selectedDiagnosticReportProviderPractitionerId: string | null;
    selectedProcedureProviderOrganizationId: string | null;
    selectedProcedureProviderPractitionerId: string | null;
    serviceId: string;
    supervisorPractitionerId?: string | null;
}

export interface ISaveAndSaveAndSendValidated {
    allergies?: IAllergies;
    assets: IAssets;
    diagnosis: IDiagnosisAddEditValidated;
    episodesAndJournals?: IEpisodesAndJournals;
    labResults?: ILabResults;
    medications?: Medications;
    request: IRequestValidated;
    validationType: RequestValidationTypes;
}

const requestBaseSchema = Koi.object({
    draftCareProviderOrganizationId: Koi.string().uuid().allow(null),
    draftCareProviderPractitionerId: Koi.string().uuid().allow(null),
    hasCostConsentPatient: Koi.boolean().optional().allow(null),
    id: Koi.string().uuid().optional(),
    patientId: Koi.string().uuid(),
    patientUsesMedication: Koi.boolean().optional().allow(null),
    previousRequestId: Koi.string().uuid().allow(null),
    question: Koi.string().max(1000).allow(null),
    serviceId: Koi.string().uuid(),
    supervisorPractitionerId: Koi.string().uuid().allow(null).optional(),
});

const procedureProvider = Koi.object({
    selectedDiagnosticReportProviderOrganizationId: Koi.valid(null),
    selectedDiagnosticReportProviderPractitionerId: Koi.valid(null),
    selectedProcedureProviderOrganizationId: Koi.string().uuid(),
    selectedProcedureProviderPractitionerId: Koi.string().uuid().allow(null),
});

const optionalProcedureProvider = Koi.object({
    selectedDiagnosticReportProviderOrganizationId: Koi.valid(null),
    selectedDiagnosticReportProviderPractitionerId: Koi.valid(null),
    selectedProcedureProviderOrganizationId: Koi.string().uuid().allow(null),
    selectedProcedureProviderPractitionerId: Koi.string().uuid().allow(null),
});

const diagnosticReportProvider = Koi.object({
    selectedDiagnosticReportProviderOrganizationId: Koi.string().uuid(),
    selectedDiagnosticReportProviderPractitionerId: Koi.string().uuid().allow(null),
    selectedProcedureProviderOrganizationId: Koi.valid(null),
    selectedProcedureProviderPractitionerId: Koi.valid(null),
});

const diagnosticReportConsumer = Koi.object({
    selectedDiagnosticReportConsumerPractitionerId: Koi.string().uuid(),
});

const optionalDiagnosticReportConsumer = Koi.object({
    selectedDiagnosticReportConsumerPractitionerId: Koi.string().uuid().allow(null),
});

const optionalDiagnosticReportProvider = Koi.object({
    selectedDiagnosticReportProviderOrganizationId: Koi.string().uuid().allow(null),
    selectedDiagnosticReportProviderPractitionerId: Koi.string().uuid().allow(null),
    selectedProcedureProviderOrganizationId: Koi.valid(null),
    selectedProcedureProviderPractitionerId: Koi.valid(null),
});

const noProvider = Koi.object({
    selectedDiagnosticReportProviderOrganizationId: Koi.valid(null),
    selectedDiagnosticReportProviderPractitionerId: Koi.valid(null),
    selectedProcedureProviderOrganizationId: Koi.valid(null),
    selectedProcedureProviderPractitionerId: Koi.valid(null),
});

export function getSaveAndSaveAndSendValidationSchema(
    requestValidationType: RequestValidationTypes
): ISchemaContainer<ISaveAndSaveAndSendValidated> {
    return {
        schema: Koi.object({
            allergies: allergies.optional(),
            assets: assets,
            diagnosis: diagnosisSchema,
            episodesAndJournals: episodesAndJournalEntriesSchema.optional(),
            labResults: labResults.optional(),
            medications: medications.optional(),
            request: getRequestSchema(requestValidationType),
            validationType: Koi.koi().enum(RequestValidationTypes).disallow(null),
        }).options({ convert: false, presence: 'required' }),
    };
}

export function getRequestValidationSchema(
    requestValidationType: RequestValidationTypes
): ISchemaContainer<IRequestValidated> {
    return {
        schema: getRequestSchema(requestValidationType).options({ convert: false, presence: 'required' }),
    };
}

function getRequestSchema(requestValidationType: RequestValidationTypes) {
    const schemas = getSchemas(requestValidationType);

    if (!schemas.customControlSchema) {
        return requestBaseSchema
            .concat(
                Koi.object({
                    customControlData: Koi.valid(null),
                })
            )
            .concat(schemas.providerSchema);
    }

    return requestBaseSchema
        .concat(
            Koi.object({
                customControlData: schemas.customControlSchema.disallow(null),
            })
        )
        .concat(schemas.providerSchema);
}

function getSchemas(requestValidationType: RequestValidationTypes) {
    /*
        For consults / referrals / referralSmalls selectedDiagnosticReportProviderOrganizationId required and selectedProcedureProviderOrganizationId has to be null
        For contact the current user will be the recipient, so no selected providers
        For GGZOint / GGZOembl the current user will be the recipient so selectedProcedureProviderOrganizationId disallowed, but selectedDiagnosticReportProviderOrganizationId is optional
        For examinations selectedProcedureProviderOrganizationId required and selectedDiagnosticReportProviderOrganizationId disallowed
    */
    let providerSchema;
    let customControlSchema;
    switch (requestValidationType) {
        case RequestValidationTypes.tpcV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = tpcV1DraftRequest;
            break;
        case RequestValidationTypes.tpcV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tpcV1FinalRequest;
            break;
        case RequestValidationTypes.tpcV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = tpcV1DraftRequest;
            break;
        case RequestValidationTypes.tpcV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tpcV1FinalRequest;
            break;

        case RequestValidationTypes.tpoV1AddFinalRequest:
            providerSchema = procedureProvider;
            break;
        case RequestValidationTypes.tpoV1EditFinalRequest:
            providerSchema = procedureProvider;
            break;
        case RequestValidationTypes.tpoV1AddDraftRequest:
            providerSchema = optionalProcedureProvider;
            break;
        case RequestValidationTypes.tpoV1EditDraftRequest:
            providerSchema = optionalProcedureProvider;
            break;

        case RequestValidationTypes.pilotExaminationV1AddFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = pilotExaminationV1FinalRequest;
            break;
        case RequestValidationTypes.pilotExaminationV1EditFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = pilotExaminationV1FinalRequest;
            break;
        case RequestValidationTypes.pilotExaminationV1AddDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = pilotExaminationV1FinalRequest;
            break;
        case RequestValidationTypes.pilotExaminationV1EditDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = pilotExaminationV1FinalRequest;
            break;

        case RequestValidationTypes.pilotReferralV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = pilotReferralV1FinalRequest;
            break;
        case RequestValidationTypes.pilotReferralV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = pilotReferralV1FinalRequest;
            break;
        case RequestValidationTypes.pilotReferralV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = pilotReferralV1FinalRequest;
            break;
        case RequestValidationTypes.pilotReferralV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = pilotReferralV1FinalRequest;
            break;

        case RequestValidationTypes.pilotReferralSmallV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = pilotReferralSmallV1FinalRequest;
            break;
        case RequestValidationTypes.pilotReferralSmallV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = pilotReferralSmallV1FinalRequest;
            break;
        case RequestValidationTypes.pilotReferralSmallV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = pilotReferralSmallV1FinalRequest;
            break;
        case RequestValidationTypes.pilotReferralSmallV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = pilotReferralSmallV1FinalRequest;
            break;

        case RequestValidationTypes.pilotContactV1AddRequest:
            providerSchema = noProvider;
            customControlSchema = pilotContactV1FinalRequest;
            break;
        case RequestValidationTypes.pilotContactV1EditRequest:
            providerSchema = noProvider;
            customControlSchema = pilotContactV1FinalRequest;
            break;

        case RequestValidationTypes.pilotConsultationV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = pilotConsultationV1FinalRequest;
            break;
        case RequestValidationTypes.pilotConsultationV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = pilotConsultationV1FinalRequest;
            break;
        case RequestValidationTypes.pilotConsultationV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = pilotConsultationV1FinalRequest;
            break;
        case RequestValidationTypes.pilotConsultationV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = pilotConsultationV1FinalRequest;
            break;

        case RequestValidationTypes.tcorV1AddDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = tcorV1DraftRequest;
            break;
        case RequestValidationTypes.tcorV1AddFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = tcorV1FinalRequest;
            break;
        case RequestValidationTypes.tcorV1EditDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = tcorV1DraftRequest;
            break;
        case RequestValidationTypes.tcorV1EditFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = tcorV1FinalRequest;
            break;

        case RequestValidationTypes.tiecgV1AddDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = tiecgV1DraftRequest;
            break;
        case RequestValidationTypes.tiecgV1AddFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = tiecgV1FinalRequest;
            break;
        case RequestValidationTypes.tiecgV1EditDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = tiecgV1DraftRequest;
            break;
        case RequestValidationTypes.tiecgV1EditFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = tiecgV1FinalRequest;
            break;

        case RequestValidationTypes.tccrV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = tccrV1DraftRequest;
            break;
        case RequestValidationTypes.tccrV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tccrV1FinalRequest;
            break;
        case RequestValidationTypes.tccrV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = tccrV1DraftRequest;
            break;
        case RequestValidationTypes.tccrV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tccrV1FinalRequest;
            break;

        case RequestValidationTypes.tcchV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = tcchV1DraftRequest;
            break;
        case RequestValidationTypes.tcchV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tcchV1FinalRequest;
            break;
        case RequestValidationTypes.tcchV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = tcchV1DraftRequest;
            break;
        case RequestValidationTypes.tcchV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tcchV1FinalRequest;
            break;

        case RequestValidationTypes.tcciV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = tcciV1DraftRequest;
            break;
        case RequestValidationTypes.tcciV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tcciV1FinalRequest;
            break;
        case RequestValidationTypes.tcciV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = tcciV1DraftRequest;
            break;
        case RequestValidationTypes.tcciV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tcciV1FinalRequest;
            break;

        case RequestValidationTypes.osasvV1AddDraftReferralSmall:
            providerSchema = diagnosticReportProvider;
            customControlSchema = osasvV1DraftReferralSmall;
            break;
        case RequestValidationTypes.osasvV1AddFinalReferralSmall:
            providerSchema = diagnosticReportProvider;
            customControlSchema = osasvV1FinalReferralSmall;
            break;
        case RequestValidationTypes.osasvV1EditDraftReferralSmall:
            providerSchema = diagnosticReportProvider;
            customControlSchema = osasvV1DraftReferralSmall;
            break;
        case RequestValidationTypes.osasvV1EditFinalReferralSmall:
            providerSchema = diagnosticReportProvider;
            customControlSchema = osasvV1FinalReferralSmall;
            break;

        case RequestValidationTypes.osasvcV1AddDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osasvcV1DraftRequest;
            break;
        case RequestValidationTypes.osasvcV1AddFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osasvcV1FinalRequest;
            break;
        case RequestValidationTypes.osasvcV1EditDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osasvcV1DraftRequest;
            break;
        case RequestValidationTypes.osasvcV1EditFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osasvcV1FinalRequest;
            break;

        case RequestValidationTypes.osasvcV2AddDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osasvcV2DraftRequest;
            break;
        case RequestValidationTypes.osasvcV2AddFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osasvcV2FinalRequest;
            break;
        case RequestValidationTypes.osasvcV2EditDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osasvcV2DraftRequest;
            break;
        case RequestValidationTypes.osasvcV2EditFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osasvcV2FinalRequest;
            break;

        case RequestValidationTypes.osasendV1AddDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osasendV1DraftRequest;
            break;
        case RequestValidationTypes.osasendV1AddFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osasendV1FinalRequest;
            break;
        case RequestValidationTypes.osasendV1EditDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osasendV1DraftRequest;
            break;
        case RequestValidationTypes.osasendV1EditFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osasendV1FinalRequest;
            break;

        case RequestValidationTypes.osasoV1AddDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osasoV1DraftRequest;
            break;
        case RequestValidationTypes.osasoV1AddFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osasoV1FinalRequest;
            break;
        case RequestValidationTypes.osasoV1EditDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osasoV1DraftRequest;
            break;
        case RequestValidationTypes.osasoV1EditFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osasoV1FinalRequest;
            break;

        case RequestValidationTypes.osasvoV1AddDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osasvoV1DraftRequest;
            break;
        case RequestValidationTypes.osasvoV1AddFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osasvoV1FinalRequest;
            break;
        case RequestValidationTypes.osasvoV1EditDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osasvoV1DraftRequest;
            break;
        case RequestValidationTypes.osasvoV1EditFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osasvoV1FinalRequest;
            break;

        case RequestValidationTypes.osasqV1AddDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osasqV1DraftRequest;
            break;
        case RequestValidationTypes.osasqV1AddFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osasqV1FinalRequest;
            break;
        case RequestValidationTypes.osasqV1EditDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osasqV1DraftRequest;
            break;
        case RequestValidationTypes.osasqV1EditFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osasqV1FinalRequest;
            break;

        case RequestValidationTypes.osaseV1AddDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osaseV1DraftRequest;
            break;
        case RequestValidationTypes.osaseV1AddFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osaseV1FinalRequest;
            break;
        case RequestValidationTypes.osaseV1EditDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osaseV1DraftRequest;
            break;
        case RequestValidationTypes.osaseV1EditFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osaseV1FinalRequest;
            break;

        case RequestValidationTypes.osaseV2AddDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osaseV2DraftRequest;
            break;
        case RequestValidationTypes.osaseV2AddFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osaseV2FinalRequest;
            break;
        case RequestValidationTypes.osaseV2EditDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osaseV2DraftRequest;
            break;
        case RequestValidationTypes.osaseV2EditFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osaseV2FinalRequest;
            break;

        case RequestValidationTypes.osasnV1AddDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osasnV1DraftRequest;
            break;
        case RequestValidationTypes.osasnV1AddFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osasnV1FinalRequest;
            break;
        case RequestValidationTypes.osasnV1EditDraftRequest:
            providerSchema = noProvider;
            customControlSchema = osasnV1DraftRequest;
            break;
        case RequestValidationTypes.osasnV1EditFinalRequest:
            providerSchema = noProvider;
            customControlSchema = osasnV1FinalRequest;
            break;

        case RequestValidationTypes.ggzcgV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzcgV1DraftRequest;
            break;
        case RequestValidationTypes.ggzcgV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = ggzcgV1FinalRequest;
            break;
        case RequestValidationTypes.ggzcgV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzcgV1DraftRequest;
            break;
        case RequestValidationTypes.ggzcgV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = ggzcgV1FinalRequest;
            break;

        case RequestValidationTypes.ggzcf2fV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzcf2fV1DraftRequest;
            break;
        case RequestValidationTypes.ggzcf2fV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = ggzcf2fV1FinalRequest;
            break;
        case RequestValidationTypes.ggzcf2fV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzcf2fV1DraftRequest;
            break;
        case RequestValidationTypes.ggzcf2fV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = ggzcf2fV1FinalRequest;
            break;

        case RequestValidationTypes.ggzctV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzctV1DraftRequest;
            break;
        case RequestValidationTypes.ggzctV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = ggzctV1FinalRequest;
            break;
        case RequestValidationTypes.ggzctV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzctV1DraftRequest;
            break;
        case RequestValidationTypes.ggzctV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = ggzctV1FinalRequest;
            break;

        case RequestValidationTypes.ggzctriageV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzctriageV1DraftRequest;
            break;
        case RequestValidationTypes.ggzctriageV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = ggzctriageV1FinalRequest;
            break;
        case RequestValidationTypes.ggzctriageV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzctriageV1DraftRequest;
            break;
        case RequestValidationTypes.ggzctriageV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = ggzctriageV1FinalRequest;
            break;

        case RequestValidationTypes.ggzcmdoV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzcmdoV1DraftRequest;
            break;
        case RequestValidationTypes.ggzcmdoV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = ggzcmdoV1FinalRequest;
            break;
        case RequestValidationTypes.ggzcmdoV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzcmdoV1DraftRequest;
            break;
        case RequestValidationTypes.ggzcmdoV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = ggzcmdoV1FinalRequest;
            break;

        case RequestValidationTypes.ggzvV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzvV1DraftRequest;
            break;
        case RequestValidationTypes.ggzvV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = ggzvV1FinalRequest;
            break;
        case RequestValidationTypes.ggzvV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzvV1DraftRequest;
            break;
        case RequestValidationTypes.ggzvV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = ggzvV1FinalRequest;
            break;

        case RequestValidationTypes.ggzoIntV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzoIntV1DraftRequest;
            break;
        case RequestValidationTypes.ggzoIntV1AddFinalRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzoIntV1FinalRequest;
            break;
        case RequestValidationTypes.ggzoIntV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzoIntV1DraftRequest;
            break;
        case RequestValidationTypes.ggzoIntV1EditFinalRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzoIntV1FinalRequest;
            break;

        case RequestValidationTypes.ggzoEmblV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzoEmblV1DraftRequest;
            break;
        case RequestValidationTypes.ggzoEmblV1AddFinalRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzoEmblV1FinalRequest;
            break;
        case RequestValidationTypes.ggzoEmblV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzoEmblV1DraftRequest;
            break;
        case RequestValidationTypes.ggzoEmblV1EditFinalRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = ggzoEmblV1FinalRequest;
            break;

        case RequestValidationTypes.ggzbTherV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportConsumer;
            customControlSchema = ggzbTherV1DraftRequest;
            break;
        case RequestValidationTypes.ggzbTherV1AddFinalRequest:
            providerSchema = diagnosticReportConsumer;
            customControlSchema = ggzbTherV1FinalRequest;
            break;
        case RequestValidationTypes.ggzbTherV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportConsumer;
            customControlSchema = ggzbTherV1DraftRequest;
            break;
        case RequestValidationTypes.ggzbTherV1EditFinalRequest:
            providerSchema = diagnosticReportConsumer;
            customControlSchema = ggzbTherV1FinalRequest;
            break;

        case RequestValidationTypes.gtoV1AddDraftRequest:
            providerSchema = noProvider;
            customControlSchema = gtoV1DraftRequest;
            break;
        case RequestValidationTypes.gtoV1AddFinalRequest:
            providerSchema = noProvider;
            customControlSchema = gtoV1FinalRequest;
            break;
        case RequestValidationTypes.gtoV1EditDraftRequest:
            providerSchema = noProvider;
            customControlSchema = gtoV1DraftRequest;
            break;
        case RequestValidationTypes.gtoV1EditFinalRequest:
            providerSchema = noProvider;
            customControlSchema = gtoV1FinalRequest;
            break;

        case RequestValidationTypes.tfoV1AddDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = tfoV1DraftRequest;
            break;
        case RequestValidationTypes.tfoV1AddFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = tfoV1FinalRequest;
            break;
        case RequestValidationTypes.tfoV1EditDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = tfoV1DraftRequest;
            break;
        case RequestValidationTypes.tfoV1EditFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = tfoV1FinalRequest;
            break;

        case RequestValidationTypes.tooV1AddDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = tooV1DraftRequest;
            break;
        case RequestValidationTypes.tooV1AddFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = tooV1FinalRequest;
            break;
        case RequestValidationTypes.tooV1EditDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = tooV1DraftRequest;
            break;
        case RequestValidationTypes.tooV1EditFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = tooV1FinalRequest;
            break;

        case RequestValidationTypes.tovV1AddDraftRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tovV1FinalRequest;
            break;
        case RequestValidationTypes.tovV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tovV1FinalRequest;
            break;
        case RequestValidationTypes.tovV1EditDraftRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tovV1FinalRequest;
            break;
        case RequestValidationTypes.tovV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tovV1FinalRequest;
            break;

        case RequestValidationTypes.tcohV1AddDraftRequest:
        case RequestValidationTypes.tcohV2AddDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = tcohV2DraftRequest;
            break;
        case RequestValidationTypes.tcohV1AddFinalRequest:
        case RequestValidationTypes.tcohV2AddFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = tcohV2FinalRequest;
            break;
        case RequestValidationTypes.tcohV1EditDraftRequest:
        case RequestValidationTypes.tcohV2EditDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = tcohV2DraftRequest;
            break;
        case RequestValidationTypes.tcohV1EditFinalRequest:
        case RequestValidationTypes.tcohV2EditFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = tcohV2FinalRequest;
            break;

        case RequestValidationTypes.tocV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = tocV1DraftRequest;
            break;
        case RequestValidationTypes.tocV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tocV1FinalRequest;
            break;
        case RequestValidationTypes.tocV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = tocV1DraftRequest;
            break;
        case RequestValidationTypes.tocV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tocV1FinalRequest;
            break;

        case RequestValidationTypes.tfcV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = tfcV1DraftRequest;
            break;
        case RequestValidationTypes.tfcV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tfcV1FinalRequest;
            break;
        case RequestValidationTypes.tfcV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = tfcV1DraftRequest;
            break;
        case RequestValidationTypes.tfcV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = tfcV1FinalRequest;
            break;

        case RequestValidationTypes.gtcV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = gtcV1DraftRequest;
            break;
        case RequestValidationTypes.gtcV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = gtcV1FinalRequest;
            break;
        case RequestValidationTypes.gtcV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = gtcV1DraftRequest;
            break;
        case RequestValidationTypes.gtcV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = gtcV1FinalRequest;
            break;

        case RequestValidationTypes.dermaCV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = dermaCV1DraftRequest;
            break;
        case RequestValidationTypes.dermaCV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = dermaCV1FinalRequest;
            break;
        case RequestValidationTypes.dermaCV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = dermaCV1DraftRequest;
            break;
        case RequestValidationTypes.dermaCV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = dermaCV1FinalRequest;
            break;

        case RequestValidationTypes.dermaSnelCV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = dermaSnelCV1DraftRequest;
            break;
        case RequestValidationTypes.dermaSnelCV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = dermaSnelCV1FinalRequest;
            break;
        case RequestValidationTypes.dermaSnelCV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = dermaSnelCV1DraftRequest;
            break;
        case RequestValidationTypes.dermaSnelCV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = dermaSnelCV1FinalRequest;
            break;

        case RequestValidationTypes.dermaCscV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = dermaCscV1DraftRequest;
            break;
        case RequestValidationTypes.dermaCscV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = dermaCscV1FinalRequest;
            break;
        case RequestValidationTypes.dermaCscV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = dermaCscV1DraftRequest;
            break;
        case RequestValidationTypes.dermaCscV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = dermaCscV1FinalRequest;
            break;

        case RequestValidationTypes.dermaSnelCscV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = dermaSnelCscV1DraftRequest;
            break;
        case RequestValidationTypes.dermaSnelCscV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = dermaSnelCscV1FinalRequest;
            break;
        case RequestValidationTypes.dermaSnelCscV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = dermaSnelCscV1DraftRequest;
            break;
        case RequestValidationTypes.dermaSnelCscV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = dermaSnelCscV1FinalRequest;
            break;

        case RequestValidationTypes.dermaCwondV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = dermaCwondV1DraftRequest;
            break;
        case RequestValidationTypes.dermaCwondV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = dermaCwondV1FinalRequest;
            break;
        case RequestValidationTypes.dermaCwondV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = dermaCwondV1DraftRequest;
            break;
        case RequestValidationTypes.dermaCwondV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = dermaCwondV1FinalRequest;
            break;

        case RequestValidationTypes.dermaOthV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = dermaOthV1DraftRequest;
            break;
        case RequestValidationTypes.dermaOthV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = dermaOthV1FinalRequest;
            break;
        case RequestValidationTypes.dermaOthV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = dermaOthV1DraftRequest;
            break;
        case RequestValidationTypes.dermaOthV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = dermaOthV1FinalRequest;
            break;

        case RequestValidationTypes.orthOV1AddDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = orthOV1DraftRequest;
            break;
        case RequestValidationTypes.orthOV1AddFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = orthOV1FinalRequest;
            break;
        case RequestValidationTypes.orthOV1EditDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = orthOV1DraftRequest;
            break;
        case RequestValidationTypes.orthOV1EditFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = orthOV1FinalRequest;
            break;

        case RequestValidationTypes.orthOFolV1AddDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = orthOFolV1DraftRequest;
            break;
        case RequestValidationTypes.orthOFolV1AddFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = orthOFolV1FinalRequest;
            break;
        case RequestValidationTypes.orthOFolV1EditDraftRequest:
            providerSchema = optionalProcedureProvider;
            customControlSchema = orthOFolV1DraftRequest;
            break;
        case RequestValidationTypes.orthOFolV1EditFinalRequest:
            providerSchema = procedureProvider;
            customControlSchema = orthOFolV1FinalRequest;
            break;
        case RequestValidationTypes.orthCV1AddFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = orthCV1FinalRequest;
            break;
        case RequestValidationTypes.orthCV1AddDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = orthCV1DraftRequest;
            break;
        case RequestValidationTypes.orthCV1EditFinalRequest:
            providerSchema = diagnosticReportProvider;
            customControlSchema = orthCV1FinalRequest;
            break;
        case RequestValidationTypes.orthCV1EditDraftRequest:
            providerSchema = optionalDiagnosticReportProvider;
            customControlSchema = orthCV1DraftRequest;
            break;

        default:
            throw new Error('Unable to find request validation schema');
    }

    if (!providerSchema) {
        throw new Error('providerSchema is missing');
    }

    return { customControlSchema, providerSchema };
}
